import { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../utils/config';
import { useAuth } from '../../Context/AuthContext';
import { uploadErrorInfo } from './arcadeTheme';
import { useGmLease } from './gmLease';

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Timeline used by ReplayPlayer (history / move_history / spin_history). */
export function replayHistoryOf(payload) {
    if (!payload || typeof payload !== 'object') return [];
    const nested = payload.entry && typeof payload.entry === 'object' ? payload.entry : null;
    const src = nested
        ? (payload.history || payload.move_history || payload.spin_history || payload.steps
            || nested.history || nested.move_history || nested.spin_history || nested.steps)
        : (payload.history || payload.move_history || payload.spin_history || payload.steps);
    return Array.isArray(src) ? src : [];
}

export function hasReplayHistory(payload) {
    return replayHistoryOf(payload).length > 0;
}

export function uploadScoreOf(json) {
    if (!json || typeof json !== 'object') return 0;
    const raw =
        json.score ??
        json.result?.score ??
        json.player_score ??
        json.result?.player_score ??
        json.bankroll ??
        json.result?.bankroll ??
        json.total_reward ??
        json.result?.total_reward ??
        json.credits ??
        json.distance ??
        json.result?.distance ??
        json.entry?.score ??
        json.entry?.total_reward ??
        0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
}

function unwrapRunPayload(json) {
    if (!json || typeof json !== 'object') return json;
    if (json.run && typeof json.run === 'object') return json.run;
    if (json.entry && typeof json.entry === 'object') return json.entry;
    if (json.data && typeof json.data === 'object') return json.data;
    return json;
}

function hasAnyReplayHistoryFields(json) {
    if (!json || typeof json !== 'object') return false;
    return Boolean(
        (Array.isArray(json.history) && json.history.length > 0)
        || (Array.isArray(json.move_history) && json.move_history.length > 0)
        || (Array.isArray(json.spin_history) && json.spin_history.length > 0)
        || (Array.isArray(json.steps) && json.steps.length > 0),
    );
}

export function normalizeUploadRun(json, { gameId, fileName } = {}) {
    if (!json || typeof json !== 'object') return null;
    if (!hasReplayHistory(json)) return null;

    // Prefer the first non-empty timeline field we can find. Some agent runners
    // return step traces under `steps` instead of `history`.
    const pickTimeline = () => {
        const nonEmptyArray = (v) => (Array.isArray(v) && v.length > 0 ? v : null);
        return nonEmptyArray(json.history)
            || nonEmptyArray(json.move_history)
            || nonEmptyArray(json.spin_history)
            || nonEmptyArray(json.steps)
            || [];
    };
    const history = pickTimeline();
    const name =
        json.agent_id ||
        json.entry?.agent_id ||
        json.agent ||
        json.username ||
        (fileName ? String(fileName).replace(/\.py$/i, '') : '') ||
        'agent';
    return {
        ...json,
        game: json.game || json.game_id || gameId,
        game_id: json.game_id || json.game || gameId,
        agent_id: name,
        score: json.score ?? json.total_reward ?? json.player_score ?? uploadScoreOf(json),
        history: Array.isArray(json.history) && json.history.length > 0 ? json.history : history,
        move_history: Array.isArray(json.move_history) && json.move_history.length > 0 ? json.move_history : history,
        spin_history: Array.isArray(json.spin_history) && json.spin_history.length > 0 ? json.spin_history : history,
        status: json.status || 'ok',
        complete: json.complete !== false,
    };
}

function normalizeUploadRunSummary(json, { gameId, fileName } = {}) {
    if (!json || typeof json !== 'object') return null;

    const nonEmptyArray = (v) => (Array.isArray(v) && v.length > 0 ? v : null);
    const timeline =
        nonEmptyArray(json.history)
        || nonEmptyArray(json.move_history)
        || nonEmptyArray(json.spin_history)
        || nonEmptyArray(json.steps)
        || [];

    const name =
        json.agent_id ||
        json.entry?.agent_id ||
        json.agent ||
        json.username ||
        (fileName ? String(fileName).replace(/\.py$/i, '') : '') ||
        'agent';

    const id = json.db_ref || json.run_id || json.id || json._id;
    const createdAt = json.timestamp ?? json.created_at ?? json.createdAt ?? json.time ?? null;

    return {
        ...json,
        id: id || undefined,
        run_id: json.run_id || json.db_ref || id || undefined,
        _id: json._id || undefined,
        game: json.game || json.game_id || gameId,
        game_id: json.game_id || json.game || gameId,
        agent_id: name,
        agent: name,
        score: json.score ?? json.total_reward ?? json.player_score ?? uploadScoreOf(json),
        history: Array.isArray(json.history) && json.history.length > 0 ? json.history : timeline,
        move_history:
            Array.isArray(json.move_history) && json.move_history.length > 0
                ? json.move_history
                : timeline,
        spin_history:
            Array.isArray(json.spin_history) && json.spin_history.length > 0
                ? json.spin_history
                : timeline,
        steps: Array.isArray(json.steps) ? json.steps : undefined,
        status: json.status || 'ok',
        complete: json.complete !== false,
        timestamp: createdAt,
        created_at: json.created_at ?? json.createdAt ?? createdAt ?? undefined,
    };
}

/**
 * Merge a poll/GET /api/runs payload into the current run.
 * An empty poll must not wipe a completed run that already has history.
 */
export function applyRunPoll(prevRun, pollJson) {
    if (hasReplayHistory(prevRun) && !hasReplayHistory(pollJson)) {
        return prevRun;
    }
    const next = normalizeUploadRun(pollJson, {
        gameId: prevRun?.game_id || prevRun?.game,
    });
    return next || prevRun || null;
}

function looksReplayableGameId(run) {
    const gameId = (run?.game_id || run?.game || '').toString().toLowerCase();
    if (!gameId) return false;
    const norm = gameId.replace(/[\s_-]/g, '');
    return (
        gameId.includes('snake')
        || gameId.includes('puzzle')
        || gameId.includes('tile')
        || gameId.includes('sliding')
        || gameId.includes('tic_tac')
        || gameId.includes('tictactoe')
        || gameId.includes('connect_four')
        || gameId.includes('connect4')
        || gameId.includes('battleship')
        || gameId.includes('lights_out')
        || gameId.includes('lightsout')
        || gameId.includes('minesweeper')
        || gameId.includes('numberlink')
        || gameId.includes('pac_man')
        || gameId.includes('pacman')
        || gameId.includes('road_fighter')
        || gameId.includes('roadfighter')
        || gameId.includes('two_joint')
        || gameId.includes('joint_pole')
        || gameId.includes('blackjack')
        || gameId.includes('bandit')
        || gameId.includes('one_armed')
        || gameId.includes('pong')
        || gameId.includes('tetris')
        || norm.includes('spaceinvader')
        || gameId.includes('adventure')
        || gameId.includes('santa')
    );
}

export function shouldShowReplay(run, isRunning = false, { only_replayable = false } = {}) {
    if (isRunning) return false;
    if (!hasReplayHistory(run)) return false;
    if (!only_replayable) return true;
    return looksReplayableGameId(run);
}

function runIdFromUpload(json) {
    if (!json || typeof json !== 'object') return null;
    const rec = unwrapRunPayload(json);
    const id = rec?.db_ref || rec?.run_id || rec?.id || rec?._id;
    return id ? String(id) : null;
}

function isPendingUpload(resp, json) {
    if (resp && resp.status === 202) return true;
    const status = String(json?.status || '').toLowerCase();
    return status === 'pending' || status === 'running' || status === 'accepted';
}

function isCompletedRunPayload(json) {
    if (!json || typeof json !== 'object') return false;
    if (json.complete === true) return true;
    if (json.complete === false) return false;
    const status = String(json.status || '').toLowerCase();
    return ['ok', 'success', 'succeeded', 'done', 'completed', 'finished', 'complete'].includes(status);
}

/**
 * Normalize a raw `/api/runs/last` submission doc into the shape the
 * "Last run" card renders. Independent of upload-response shape so the card
 * is always driven by the same server document, regardless of who/what
 * produced it (agent upload, manual play, another browser tab, etc.).
 */
export function normalizeLastRunDoc(doc) {
    if (!doc || typeof doc !== 'object') return null;
    const name = doc.agent_id || doc.username || doc.player_id || 'agent';
    const time = doc.timestamp || doc.created_at || doc.createdAt || doc.time || null;
    const timeline = replayHistoryOf(doc);
    return {
        ...doc,
        id: doc._id || doc.id || doc.run_id || undefined,
        name,
        score: uploadScoreOf(doc),
        time,
        history: Array.isArray(doc.history) ? doc.history : timeline,
        move_history: Array.isArray(doc.move_history) ? doc.move_history : timeline,
        spin_history: Array.isArray(doc.spin_history) ? doc.spin_history : timeline,
        steps: Array.isArray(doc.steps) ? doc.steps : undefined,
        raw: doc,
    };
}

/**
 * Shared Arcade session helpers: dashboard leaderboard + authenticated agent upload.
 * Matches Snake / PuzzleMaster auth + API wiring for GM-backed games.
 */
export function useArcadeAgentSession({ gameId, uploadUrl, onUploadSuccess }) {
    const { token, user } = useAuth();
    // Shared game server for manual play: "Starting game…" / start errors, and
    // the lease is released when the game page unmounts (gmLease.js).
    const gmLease = useGmLease(gameId);
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [agentFile, setAgentFile] = useState(null);
    const [agentInputTab, setAgentInputTab] = useState('upload');
    const [agentRunLoading, setAgentRunLoading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    const [uploadNeedsAccount, setUploadNeedsAccount] = useState(false);
    // Set by useManualRunRecorder when a manual run was not saved (shown by ArcadeGameShell).
    const [manualSaveNotice, setManualSaveNotice] = useState(null);
    const [lastExecuted, setLastExecuted] = useState(null);
    const [run, setRun] = useState(null);
    const uploadInFlightRef = useRef(false);

    // --- Persistent "Last run" card: always server-sourced, never derived
    // from ephemeral upload-response state. This is what eliminates the
    // cross-browser flicker — the card's content comes from a deterministic
    // GET that runs on mount and after upload, not from timing-sensitive
    // local state that can be cleared by unrelated re-renders/remounts.
    const [lastRun, setLastRun] = useState(null);
    const [lastRunLoading, setLastRunLoading] = useState(true);
    const [lastRunError, setLastRunError] = useState(null);
    const lastRunRequestIdRef = useRef(0);

    const userId = user?._id || user?.id || null;
    const userRole = user?.user_type || user?.role || (token ? 'registered' : 'guest');

    const fetchLastRun = useCallback(async () => {
        if (!gameId) {
            setLastRun(null);
            setLastRunLoading(false);
            return;
        }
        const requestId = lastRunRequestIdRef.current + 1;
        lastRunRequestIdRef.current = requestId;
        setLastRunLoading(true);
        setLastRunError(null);
        try {
            const url = API_ENDPOINTS.LAST_RUN(gameId, { userId, role: userRole });
            const res = await fetch(url);
            if (!res.ok) throw new Error(`last run fetch failed (${res.status})`);
            const data = await res.json();
            // Ignore stale responses from superseded/overlapping requests
            // (e.g. rapid remounts or user-id changes mid-flight).
            if (lastRunRequestIdRef.current !== requestId) return;
            setLastRun(normalizeLastRunDoc(data?.run));
        } catch (err) {
            if (lastRunRequestIdRef.current !== requestId) return;
            console.warn('Arcade last-run fetch failed', gameId, err);
            setLastRunError(err);
            // Keep whatever was last successfully fetched rather than
            // clearing the card on a transient network hiccup.
        } finally {
            if (lastRunRequestIdRef.current === requestId) {
                setLastRunLoading(false);
            }
        }
    }, [gameId, userId, userRole]);

    // Fetch on mount (and whenever game/user identity changes) so the card
    // is populated deterministically, independent of any upload happening
    // in this session.
    useEffect(() => {
        fetchLastRun();
    }, [fetchLastRun]);

    const refreshLeaderboard = useCallback(async () => {
        if (!gameId) {
            setLeaderboard([]);
            return;
        }
        setLeaderboardLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.LEADERBOARD(gameId));
            if (!res.ok) throw new Error('leaderboard failed');
            const data = await res.json();
            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.leaderboard)
                    ? data.leaderboard
                    : [];
            setLeaderboard(list);
        } catch (err) {
            console.warn('Arcade leaderboard fetch failed', gameId, err);
            setLeaderboard([]);
        } finally {
            setLeaderboardLoading(false);
        }
    }, [gameId]);

    useEffect(() => {
        refreshLeaderboard();
    }, [refreshLeaderboard]);

    const handleAgentFileChange = useCallback((event) => {
        const file = event.target.files && event.target.files[0];
        setAgentFile(file || null);
        setUploadMessage(file ? `Selected ${file.name}` : '');
    }, []);

    const pollRunDetail = useCallback(
        async (runId, { fileName } = {}) => {
            if (!runId) return { replayRun: null, summaryRun: null };
            let mergedReplay = null;
            let lastSummary = null;

            for (let i = 0; i < 8; i += 1) {
                try {
                    const res = await fetch(API_ENDPOINTS.RUN_DETAIL(runId), {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (!res.ok) throw new Error(`status ${res.status}`);

                    const data = await res.json();
                    const rec = data.run || data.entry || data;

                    mergedReplay = applyRunPoll(mergedReplay, rec);
                    lastSummary = normalizeUploadRunSummary(rec, { gameId, fileName });

                    if (hasReplayHistory(mergedReplay)) {
                        return { replayRun: mergedReplay, summaryRun: lastSummary };
                    }

                    if (lastSummary && isCompletedRunPayload(rec)) {
                        // Completed run without replay history: still enough for the "Last run" card.
                        return { replayRun: null, summaryRun: lastSummary };
                    }
                } catch (err) {
                    console.warn('Arcade run poll failed', runId, err);
                }
                await sleep(400);
            }

            return {
                replayRun: hasReplayHistory(mergedReplay) ? mergedReplay : null,
                summaryRun: lastSummary,
            };
        },
        [token, gameId]
    );

    const runAgentUpload = useCallback(async (fileOverride) => {
        if (uploadInFlightRef.current) return null;
        if (!token) {
            setUploadMessage('Sign in to upload an agent.');
            return null;
        }
        const file = fileOverride || agentFile;
        if (!file) {
            setUploadMessage('Select an agent file first.');
            return null;
        }
        if (!uploadUrl) {
            setUploadMessage('Upload endpoint is not configured.');
            return null;
        }

        uploadInFlightRef.current = true;
        setAgentRunLoading(true);
        setUploadMessage('Running Agent');
        setUploadNeedsAccount(false);
        try {
            const form = new FormData();
            form.append('file', file);
            const resp = await fetch(uploadUrl, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            let json = {};
            try {
                json = await resp.json();
            } catch {
                json = {};
            }

            if (!resp.ok && resp.status !== 202) {
                const { message, needsAccount } = uploadErrorInfo(
                    resp.status, json, 'Agent execution failed.'
                );
                setUploadMessage(message);
                setUploadNeedsAccount(needsAccount);
                return null;
            }

            const uploadRec = unwrapRunPayload(json);
            let replayRun = normalizeUploadRun(uploadRec, { gameId, fileName: file.name });
            let summaryRun = normalizeUploadRunSummary(uploadRec, { gameId, fileName: file.name });

            // Replay data is optional for the "Last run" card. If we don't have it yet,
            // poll for completion to fill in score/timestamp too.
            if (!replayRun && (isPendingUpload(resp, json) || !hasAnyReplayHistoryFields(uploadRec))) {
                const pollId = runIdFromUpload(json);
                if (pollId) {
                    const polled = await pollRunDetail(pollId, { fileName: file.name });
                    replayRun = polled.replayRun || replayRun;
                    summaryRun = polled.summaryRun || summaryRun;
                    if (replayRun) {
                        replayRun = applyRunPoll(replayRun, uploadRec) || replayRun;
                    }
                }
            }

            const finalRun = replayRun || summaryRun;
            if (!finalRun) return null;

            const score = uploadScoreOf(finalRun);
            const name =
                finalRun.agent_id ||
                uploadRec.agent_id ||
                file.name.replace(/\.py$/i, '') ||
                'agent';

            const time =
                finalRun.timestamp
                || finalRun.created_at
                || finalRun.createdAt
                || finalRun.time
                || Date.now();

            setRun(finalRun);
            setLastExecuted({
                name,
                score,
                time,
                result: uploadRec.result,
            });
            setUploadMessage(
                replayRun
                    ? `Agent finished — score ${score}`
                    : `Agent finished — score ${score} (no replay)`,
            );
            await refreshLeaderboard();
            // Refresh the persistent card from the backend rather than trusting
            // this in-memory upload result directly — the fresh upload is only
            // a *trigger* to refetch, not the source of the displayed data.
            await fetchLastRun();
            if (typeof onUploadSuccess === 'function') {
                await onUploadSuccess(finalRun);
            }
            return finalRun;
        } catch (err) {
            console.error('Agent upload failed', err);
            setUploadMessage('Upload failed. Try again later.');
            return null;
        } finally {
            uploadInFlightRef.current = false;
            setAgentRunLoading(false);
        }
    }, [token, agentFile, uploadUrl, refreshLeaderboard, fetchLastRun, onUploadSuccess, gameId, pollRunDetail]);

    const handleRunAgent = useCallback(() => runAgentUpload(), [runAgentUpload]);

    const handleEditorSubmit = useCallback(
        (code) => {
            const blob = new Blob([code], { type: 'text/x-python' });
            const file = new File([blob], 'agent.py', { type: 'text/x-python' });
            setAgentFile(file);
            setTimeout(() => {
                runAgentUpload(file);
            }, 0);
        },
        [runAgentUpload]
    );

    return {
        token,
        gameId,
        gmLease,
        leaderboard,
        leaderboardLoading,
        refreshLeaderboard,
        agentFile,
        setAgentFile,
        agentInputTab,
        setAgentInputTab,
        agentRunLoading,
        uploadMessage,
        setUploadMessage,
        uploadNeedsAccount,
        manualSaveNotice,
        setManualSaveNotice,
        lastExecuted,
        lastRun,
        lastRunLoading,
        lastRunError,
        refreshLastRun: fetchLastRun,
        run,
        handleAgentFileChange,
        handleRunAgent,
        handleEditorSubmit,
    };
}
